import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, ShieldCheck, Truck } from "lucide-react";
import Seo from "../components/Seo";
import EnquiryForm from "../components/EnquiryForm";
import { Reveal } from "../components/Reveal";
import { SITE, waLink } from "../data/site";

const Enquiry = () => {
  const [params] = useSearchParams();
  const product = useMemo(() => params.get("product") || "", [params]);

  return (
    <>
      <Seo
        title="Contact Us | Cable Jointing Kit Supplier Delhi — Universal Engineers"
        description="Contact Universal Engineers — 1503, Bhagirath Palace, Chandni Chowk, New Delhi. Call +91 9811107174 or email universalengineers.ncg@gmail.com for 3M cable jointing kits, heat shrink joints and LV/HT termination kits."
        path="/enquiry"
        jsonld={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Universal Engineers — Cable Jointing Kit Supplier Delhi",
          url: `${SITE.domain}/enquiry`,
          mainEntity: {
            "@type": "LocalBusiness",
            name: "Universal Engineers",
            telephone: "+91-9811107174",
            email: "universalengineers.ncg@gmail.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "1503, Bhagirath Palace, Chandni Chowk",
              addressLocality: "New Delhi",
              postalCode: "110006",
              addressCountry: "IN",
            },
          },
        }}
      />

      <section className="grain relative overflow-hidden bg-navy pb-16 pt-32 sm:pb-20 lg:pt-40" data-testid="enquiry-hero">
        <div className="bp-grid absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 sm:text-xs">
              <span className="inline-block h-2 w-2 bg-safety" /> RFQ & Enquiries
            </p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Get a quote by next working day, first half.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Share your requirement — voltage class, quantity, delivery
              location — and our team responds with pricing and stock
              position.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="enquiry-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <div className="border border-slate-200 bg-white p-6 sm:p-9" data-testid="enquiry-form-card">
              {product && (
                <p className="mb-6 border-l-2 border-safety bg-safety-tint px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-navy" data-testid="enquiry-prefill-note">
                  Enquiring about: {product.replace(/-/g, " ")}
                </p>
              )}
              <EnquiryForm defaultProduct={product} />
            </div>
          </Reveal>

          <div className="space-y-5">
            <Reveal delay={0.08}>
              <div className="border-l-2 border-safety bg-safety-tint p-6" data-testid="response-promise-card">
                <p className="font-display text-lg font-bold text-navy">What happens after you send?</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Your enquiry is logged and acknowledged instantly by email.
                  Our team will respond by <strong>next working day, first
                  half</strong> with your quote.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="border border-slate-200 bg-white p-6" data-testid="direct-connect-card">
                <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-safety">Direct connect</h2>
                <div className="mt-5 space-y-3.5">
                  <a href={SITE.phoneHref} data-testid="enquiry-call-link" className="group flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center border border-slate-200 transition-colors group-hover:border-safety group-hover:bg-safety-tint">
                      <Phone size={18} className="text-navy group-hover:text-safety" />
                    </span>
                    <span>
                      <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Call us</span>
                      <span className="font-display font-bold text-navy">{SITE.phoneDisplay}</span>
                    </span>
                  </a>
                  <a href={SITE.landlineHref} data-testid="enquiry-landline-link" className="group flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center border border-slate-200 transition-colors group-hover:border-safety group-hover:bg-safety-tint">
                      <Phone size={18} className="text-navy group-hover:text-safety" />
                    </span>
                    <span>
                      <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Landline</span>
                      <span className="font-display font-bold text-navy">{SITE.landlineDisplay}</span>
                    </span>
                  </a>
                  <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="enquiry-whatsapp-link" className="group flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center border border-slate-200 transition-colors group-hover:border-[#25D366] group-hover:bg-[#25D366]/10">
                      <MessageCircle size={18} className="text-[#25D366]" />
                    </span>
                    <span>
                      <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">WhatsApp</span>
                      <span className="font-display font-bold text-navy">Chat with the sales desk</span>
                    </span>
                  </a>
                  <a href={`mailto:${SITE.email}`} data-testid="enquiry-email-link" className="group flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center border border-slate-200 transition-colors group-hover:border-safety group-hover:bg-safety-tint">
                      <Mail size={18} className="text-navy group-hover:text-safety" />
                    </span>
                    <span>
                      <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">Email</span>
                      <span className="break-all font-display font-bold text-navy">{SITE.email}</span>
                    </span>
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="border border-slate-200 bg-white p-6" data-testid="location-card">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-safety">Visit us</h2>
                  <a href={SITE.mapLink} target="_blank" rel="noopener noreferrer" data-testid="enquiry-map-open-link" className="font-mono text-[10px] uppercase tracking-[0.16em] text-navy underline underline-offset-4 hover:text-safety">
                    Open in Maps
                  </a>
                </div>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  <p className="flex items-start gap-2.5">
                    <MapPin size={15} className="mt-0.5 shrink-0 text-safety" />
                    <span>
                      <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Head Office</span>
                      {SITE.addressLine1}<br />{SITE.addressLine2}
                    </span>
                  </p>
                  <p className="flex items-start gap-2.5">
                    <MapPin size={15} className="mt-0.5 shrink-0 text-safety" />
                    <span>
                      <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Branch</span>
                      {SITE.address2Line1}<br />{SITE.address2Line2}
                    </span>
                  </p>
                  <p className="flex items-center gap-2.5">
                    <Clock size={15} className="shrink-0 text-safety" />
                    {SITE.hours}
                  </p>
                </div>
                <div className="mt-5 border border-slate-200" data-testid="enquiry-map-embed">
                  <iframe
                    title="Universal Engineers — Bhagirath Palace, Delhi"
                    src={SITE.mapEmbed}
                    className="h-56 w-full grayscale-[0.4]"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="grid grid-cols-2 gap-5">
                <div className="border border-slate-200 p-5" data-testid="logistics-ncr">
                  <Truck size={18} className="text-safety" />
                  <p className="mt-2.5 font-display text-sm font-bold text-navy">Same-day NCR</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">Delhi-NCR orders process same day.</p>
                </div>
                <div className="border border-slate-200 p-5" data-testid="logistics-india">
                  <ShieldCheck size={18} className="text-safety" />
                  <p className="mt-2.5 font-display text-sm font-bold text-navy">PAN India</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">24–48h North India, 3–5 days nationwide.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Enquiry;
